import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const arrowSx = {
  position: "absolute",
  top: "42%",
  transform: "translateY(-50%)",
  zIndex: 10,
  width: 40,
  height: 40,
  bgcolor: "#fff",
  color: "#111",
  border: "1px solid rgba(200,160,84,0.5)",
  boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
  "&:hover": { bgcolor: "#C8A054", color: "#fff" },
};

// A horizontal row of cards with no scroll bar: previous / next buttons scroll it instead.
// A button only shows when there is more to see in that direction.
const ScrollRow = ({ children, gap = 2 }) => {
  const ref = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, children]);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <Box sx={{ position: "relative" }}>
      {canPrev && (
        <IconButton aria-label="Previous" onClick={() => scrollBy(-1)} sx={{ ...arrowSx, left: { xs: -6, md: -18 } }}>
          <ChevronLeftIcon />
        </IconButton>
      )}
      <Box
        ref={ref}
        sx={{
          display: "flex",
          gap,
          overflowX: "auto",
          pt: 1.25,
          px: 0.75,
          mx: -0.75,
          pb: 2,
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {children}
      </Box>
      {canNext && (
        <IconButton aria-label="Next" onClick={() => scrollBy(1)} sx={{ ...arrowSx, right: { xs: -6, md: -18 } }}>
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default ScrollRow;
